import React, { useMemo, useState, useEffect } from "react";
import { Minus, Plus, X } from "lucide-react";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { VegDot } from "@/components/common";
import { inr } from "@/lib/api";

export function CustomizeSheet({ item, restaurant, open, onClose, onAdd }) {
  const [qty, setQty] = useState(1);
  const [variant, setVariant] = useState(null);
  const [addons, setAddons] = useState([]);
  const [spice, setSpice] = useState(null);
  const [instructions, setInstructions] = useState("");

  useEffect(() => {
    if (open && item) {
      setQty(1);
      setVariant(item.variants?.length ? item.variants[0].name : null);
      setAddons([]);
      setSpice(item.spice_levels?.length ? item.spice_levels[0] : null);
      setInstructions("");
    }
  }, [open, item]);

  const unit = useMemo(() => {
    if (!item) return 0;
    let base = item.price;
    if (variant) { const v = item.variants.find((x) => x.name === variant); if (v) base = v.price; }
    base = Math.round(base * (1 - (item.discount_pct || 0) / 100));
    const addTotal = (item.addons || []).filter((a) => addons.includes(a.name)).reduce((s, a) => s + a.price, 0);
    return base + addTotal;
  }, [item, variant, addons]);

  if (!item) return null;
  const toggleAddon = (name) => setAddons((p) => (p.includes(name) ? p.filter((x) => x !== name) : [...p, name]));

  const submit = async () => {
    await onAdd(restaurant, {
      menu_item_id: item.id, name: item.name, image: item.image, veg: item.veg,
      qty, variant, addons, spice, instructions, unit_total: unit,
    });
    onClose();
  };

  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose()}>
      <SheetContent side="bottom" className="max-w-[520px] mx-auto rounded-t-3xl p-0 max-h-[88vh] overflow-y-auto" data-testid="customize-sheet">
        <SheetTitle className="sr-only">{item.name}</SheetTitle>
        <div className="relative">
          {item.image && <img src={item.image} alt={item.name} className="w-full h-40 object-cover" />}
          <button onClick={onClose} className="absolute top-3 right-3 w-8 h-8 bg-white rounded-full flex items-center justify-center shadow" data-testid="close-customize"><X size={18} /></button>
        </div>
        <div className="p-5">
          <div className="flex items-center gap-2"><VegDot veg={item.veg} /><h3 className="font-display font-bold text-xl text-ink">{item.name}</h3></div>
          <p className="text-sm text-ink-600 mt-1">{item.description}</p>

          {item.variants?.length > 0 && (
            <div className="mt-5">
              <h4 className="font-bold text-ink text-sm mb-2">Choose size <span className="text-coral">*</span></h4>
              <div className="space-y-2">
                {item.variants.map((v) => (
                  <label key={v.name} className="flex items-center justify-between bg-surface rounded-xl px-3 py-2.5 cursor-pointer" data-testid={`variant-${v.name}`}>
                    <span className="text-sm font-medium">{v.name}</span>
                    <span className="flex items-center gap-2 text-sm font-semibold">{inr(v.price)}
                      <input type="radio" name="variant" checked={variant === v.name} onChange={() => setVariant(v.name)} className="accent-coral w-4 h-4" />
                    </span>
                  </label>
                ))}
              </div>
            </div>
          )}

          {item.addons?.length > 0 && (
            <div className="mt-5">
              <h4 className="font-bold text-ink text-sm mb-2">Add-ons</h4>
              <div className="space-y-2">
                {item.addons.map((a) => (
                  <label key={a.name} className="flex items-center justify-between bg-surface rounded-xl px-3 py-2.5 cursor-pointer" data-testid={`addon-${a.name}`}>
                    <span className="text-sm font-medium">+ {a.name}</span>
                    <span className="flex items-center gap-2 text-sm font-semibold">{inr(a.price)}
                      <input type="checkbox" checked={addons.includes(a.name)} onChange={() => toggleAddon(a.name)} className="accent-coral w-4 h-4" />
                    </span>
                  </label>
                ))}
              </div>
            </div>
          )}

          {item.spice_levels?.length > 0 && (
            <div className="mt-5">
              <h4 className="font-bold text-ink text-sm mb-2">Spice level</h4>
              <div className="flex gap-2 flex-wrap">
                {item.spice_levels.map((s) => (
                  <button key={s} onClick={() => setSpice(s)} className={`px-3 py-1.5 rounded-full text-sm font-semibold border ${spice === s ? "bg-coral text-white border-coral" : "bg-white border-border text-ink-600"}`} data-testid={`spice-${s}`}>{s}</button>
                ))}
              </div>
            </div>
          )}

          <div className="mt-5">
            <h4 className="font-bold text-ink text-sm mb-2">Special instructions</h4>
            <input value={instructions} onChange={(e) => setInstructions(e.target.value)} placeholder="e.g. Less oil, no onions" className="w-full bg-surface rounded-xl px-3 py-2.5 text-sm outline-none focus:ring-2 ring-coral" data-testid="instructions-input" />
          </div>
        </div>

        <div className="sticky bottom-0 bg-white border-t border-border p-4 flex items-center gap-3">
          <div className="flex items-center bg-surface rounded-xl">
            <button onClick={() => setQty((q) => Math.max(1, q - 1))} className="w-10 h-10 flex items-center justify-center text-coral" data-testid="qty-minus"><Minus size={18} /></button>
            <span className="w-8 text-center font-bold" data-testid="qty-value">{qty}</span>
            <button onClick={() => setQty((q) => q + 1)} className="w-10 h-10 flex items-center justify-center text-coral" data-testid="qty-plus"><Plus size={18} /></button>
          </div>
          <button onClick={submit} className="flex-1 bg-coral hover:bg-coral-hover text-white font-bold py-3 rounded-xl tap" data-testid="add-to-cart-confirm">
            Add item · {inr(unit * qty)}
          </button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
